/* eslint-disable default-case */
import React, { useEffect, useRef, useState } from 'react'
import { Button, Table, notification, Row, Col, Dropdown, Space, Checkbox, Menu } from 'antd'
import useWindowDimensions from '../../../../hooks/getWindowDimensions'
import { CloudUploadOutlined, DownOutlined, ExportOutlined, Html5Outlined, PieChartOutlined, PrinterOutlined } from '@ant-design/icons'

import './css/tournament-final.css'
import { useReactToPrint } from 'react-to-print'
import _ from 'lodash'
import PrintFinal from './PrintFinal'
import { finalColumns } from '../../../../shared/tables'
import { promoteParticipants } from '../../../../shared/tournamentRating'
import GraphFinal from './GraphFinal'
import calculateFeats from '../../../../shared/feats'
import Feats from './Feats'
import RaffleListDisplay from './RaffleListDisplay'

const buildConfig = (tournament, round, index) => {
  const configValues = [
    'duration',
    'name',
    'promotion',
    'promotion_exact',
    'score',
    'bestof',
    'fenceoff'
  ]
  let configPrefix = round
  if (round === 'pools') {
    configPrefix = 'pool'
  }
  if (round === 'bestof') {
    configPrefix = 'table'
  }

  const config = {}

  _.each(configValues, (value) => {
    config[value] = _.get(tournament, `${configPrefix}_${value}_${index}`)
  })

  return config
}

const buildScheduleName = (name, index) => {
  let scheduleName = `tournament.${name}_${index}`
  if (name === 'bestof') {
    scheduleName = `tournament.table_${index}`
  }
  return scheduleName
}

const TournamentFinal = (props) => {
  const { height } = useWindowDimensions()
  const printFinalRef = useRef()
  const [tournament, setTournament] = useState({})
  const [isLoaded, setIsLoaded] = useState(false)
  const [sorted, setSorted] = useState([])
  const [resultColumns, setResultColumns] = useState(finalColumns)
  const [awardedFeats, setAwardedFeats] = useState()
  const [isResultColumnSelectorVisible, setIsResultColumnSelectorVisible] = useState(false)
  const [isResultGraphVisible, setIsResultGraphVisible] = useState(false)
  const [isResultFeatsVisible, setIsResultFeatsVisible] = useState(false)
  const [isResultHonorVisible, setIsResultHonorVisible] = useState(false)

  const handlePrintFinal = useReactToPrint({
    content: () => printFinalRef.current
  })

  const dynamicSizing = {
    y: height - 450
  }

  useEffect(() => {
    const { schedule } = props.tournament
    const tournamentParticipants = props.tournament.participants.map((p, index) => {
      const value = _.pick(p, ['age', 'club', 'demographic', 'first_name', 'last_name', 'id', 'rating', 'fid'])
      if (p.currentSeed) {
        value.place = p.currentSeed
      } else {
        value.place = props?.tournament?.participants?.length
      }

      return value
    })

    const tournamentValue = tournamentParticipants.reduce((previousValue, participant) => {
      let value = 0
      switch (participant.rating) {
        case 'A': {
          value += 50
          break
        }
        case 'B': {
          value += 40
          break
        }
        case 'C': {
          value += 30
          break
        }
        case 'D': {
          value += 20
          break
        }
        case 'E': {
          value += 10
          break
        }
        default: {
          value += 1
        }
      }
      return previousValue + value
    }, 0)

    const tournamentBracketWinValue = _.get(props.tournament, 'tournamentBracketWinValue', 20)
    let lowestTournamentPoints

    const calculatedRawParticipants = tournamentParticipants.map(participant => {
      let bracketPoints = 0
      participant.stats = {
        standard: {
          wins: 0,
          loses: 0,
          for: 0,
          against: 0
        },
        advanced: {
          for: {},
          against: {}
        },
        flawless: 0,
        onePoint: 0,
        timeOut: 0,
        honor: 0,
        poolWins: 0,
        eliminationWins: 0
      }

      participant.feats = []
      participant.poolScore = 0
      participant.tournamentScore = 0
      participant.tournamentPoints = 0
      participant.tournamentPointsAdjusted = 0

      _.each(schedule, (scheduleItem, scheduleIndex) => {
        const config = buildConfig(props.tournament, scheduleItem, scheduleIndex)
        const maxScore = _.toInteger(config.score)
        const promotion = _.toInteger(config.promotion)
        const data = _.get(props, buildScheduleName(scheduleItem, scheduleIndex))

        // handle pool scoring
        if (scheduleItem === 'pools') {
          let wins = 0
          _.each(data, (pool) => {
            const index = _.findIndex(pool.participants, { uuid: participant.id })

            if (index > -1) {
              participant.poolScore = pool.participants[index].stats.points
              const advancedStats = _.filter(pool.advanced, (o) => !_.isUndefined(o[index + 1]))

              participant.stats.honor += _.get(pool.participants[index], 'honor', 0)

              // handle pool advanced stats
              _.each(advancedStats, (match) => {
                _.each(Object.keys(match), (key) => {
                  if (_.isArray(match[key])) {
                    _.each(match[key], (exchange) => {
                      if (exchange !== 'x' && exchange !== null) {
                        if (_.toInteger(key) === index + 1) {
                          if (exchange !== 'H') {
                            const initialValue = participant?.stats?.advanced?.for?.[exchange] || 0
                            participant.stats.advanced.for[exchange] = initialValue + 1
                          }
                        } else {
                          const initialValue = participant?.stats?.advanced?.against?.[exchange] || 0
                          participant.stats.advanced.against[exchange] = initialValue + 1
                          if (exchange === 'H') {
                            participant.stats.honor += 1
                          }
                        }
                      }
                    })
                  }
                })
              })

              _.each(pool.scores, (row, rowIndex, collection) => {
                if (rowIndex === index) {
                  _.each(row, (matchScore, matchIndex) => {
                    if (matchScore === 'V') {
                      participant.stats.standard.for += maxScore
                      participant.stats.standard.wins += 1
                      wins += 1
                      if (pool.scores[matchIndex][rowIndex] === 0 || pool.scores[matchIndex][rowIndex] === '0') {
                        participant.stats.flawless += 1
                      }
                      if (pool.scores[matchIndex][rowIndex] === maxScore - 1) {
                        participant.stats.onePoint += 1
                      }
                    } else if (_.first(matchScore) === 'V') {
                      participant.stats.standard.for += _.toInteger(matchScore.slice(1))
                      participant.stats.standard.wins += 1
                      wins += 1
                      if (pool.scores[matchIndex][rowIndex] === 0 || pool.scores[matchIndex][rowIndex] === '0') {
                        participant.stats.flawless += 1
                      }
                      if (pool.scores[matchIndex][rowIndex] === maxScore - 1) {
                        participant.stats.onePoint += 1
                      }
                    } else {
                      participant.stats.standard.for += _.toInteger(matchScore)
                    }
                  })
                } else {
                  if (row[index] === 'V') {
                    participant.stats.standard.against += maxScore
                    participant.stats.standard.loses += 1
                  } else if (_.first(row[index]) === 'V') {
                    participant.stats.standard.against += _.toInteger(row[index].slice(1))
                    participant.stats.standard.loses += 1
                  } else {
                    participant.stats.standard.against += _.toInteger(row[index])
                  }
                }
              })
            }
          })

          participant.stats.poolWins = wins
        }
        if (scheduleItem === 'table') {
          const tableStats = {
            wins: 0,
            semifinal: 0
          }
          _.each(data, (table) => {
            // only parse stats up to promotion if set
            if (table.id > promotion || !promotion) {
              bracketPoints += tournamentBracketWinValue
              const roundSeeds = _.filter(table.seeds, (seed) => seed.teams.some(team => team.id === participant.id))

              _.each(roundSeeds, (seed) => {
                _.each(seed.teams, (team, teamIndex) => {
                  participant.stats.honor += _.get(team, 'honor', 0)

                  if (team.advanced) {
                    _.each(team.advanced, (exchange) => {
                      if (exchange !== 'x' && exchange !== null) {
                        if (team.id === participant.id) {
                          if (exchange !== 'H') {
                            const initialValue = participant?.stats?.advanced?.for?.[exchange] || 0
                            participant.stats.advanced.for[exchange] = initialValue + 1
                          }
                        } else {
                          const initialValue = participant?.stats?.advanced?.against?.[exchange] || 0
                          participant.stats.advanced.against[exchange] = initialValue + 1
                          if (exchange === 'H') {
                            participant.stats.honor += 1
                          }
                        }
                      }
                    })
                  }

                  if (team.id === participant.id) {
                    if (team.score === 'V' || team.score === maxScore) {
                      participant.stats.standard.for += maxScore
                      participant.stats.standard.wins += 1
                      tableStats.wins += 1
                      if (table.id <= 4) {
                        tableStats.semifinal += 1
                      }
                      if (seed.teams[1 - teamIndex].score === 0) {
                        participant.stats.flawless += 1
                      }
                      if (seed.teams[1 - teamIndex].score === maxScore - 1) {
                        participant.stats.onePoint += 1
                      }
                    } else if (_.first(team.score) === 'V' || team.score > maxScore || team.isWinner) {
                      if (_.first(team.score) === 'V') {
                        participant.stats.standard.for += _.toInteger(team.score.slice(1))
                      } else {
                        participant.stats.standard.for += team.score
                      }
                      participant.stats.standard.wins += 1
                      tableStats.wins += 1
                      if (table.id <= 4 && table.id !== 3) {
                        tableStats.semifinal += 1
                      }
                      if (seed.teams[1 - teamIndex].score === 0) {
                        participant.stats.flawless += 1
                      }
                      if (seed.teams[1 - teamIndex].score === maxScore - 1) {
                        participant.stats.onePoint += 1
                      }
                    } else {
                      participant.stats.standard.for += _.toInteger(team.score)
                      tableStats.for += _.toInteger(team.score)
                    }
                  } else {
                    if (team.score === 'V' || team.score === maxScore) {
                      participant.stats.standard.against += maxScore
                      participant.stats.standard.loses += 1
                    } else if (_.first(team.score) === 'V' || team.score > maxScore) {
                      if (_.first(team.score) === 'V') {
                        participant.stats.standard.against += _.toInteger(team.score.slice(1))
                      } else {
                        participant.stats.standard.against += team.score
                      }
                      participant.stats.standard.loses += 1
                    } else {
                      participant.stats.standard.against += _.toInteger(team.score)
                    }
                  }
                })
              })
            }
          })

          participant.stats.eliminationWins = tableStats.wins
          participant.tournamentScore += tableStats.wins * tournamentBracketWinValue
        }
        if (scheduleItem === 'bestof') {
          const tableStats = {
            wins: 0,
            semifinal: 0
          }
          _.each(data, (table) => {
            if (table.id > promotion || !promotion) {
              bracketPoints += (tournamentBracketWinValue / 2) * config.bestof
              const roundSeeds = _.filter(table.seeds, (seed) => seed.teams.some(team => team.id === participant.id))

              _.each(roundSeeds, (seed) => {
                _.each(seed.teams, (team) => {
                  _.each(team.bestof, (match) => {
                    if (match.advanced) {
                      _.each(match.advanced, (exchange) => {
                        if (exchange !== 'x' && exchange !== null) {
                          if (team.id === participant.id) {
                            if (exchange !== 'H') {
                              const initialValue = participant?.stats?.advanced?.for?.[exchange] || 0
                              participant.stats.advanced.for[exchange] = initialValue + 1
                            }
                          } else {
                            const initialValue = participant?.stats?.advanced?.against?.[exchange] || 0
                            participant.stats.advanced.against[exchange] = initialValue + 1
                            if (exchange === 'h') {
                              participant.stats.honor += 1
                            }
                          }
                        }
                      })
                    }

                    if (team.id === participant.id) {
                      if (match.score === 'V') {
                        participant.stats.standard.for += maxScore
                        participant.stats.standard.wins += 1
                        tableStats.wins += 1
                        if (table.id <= 4 && table.id !== 3) {
                          tableStats.semifinal += 1
                        }
                      } else if (_.first(match.score) === 'V') {
                        participant.stats.standard.for += _.toInteger(match.score.slice(1))
                        participant.stats.standard.wins += 1
                        tableStats.wins += 1
                        if (table.id <= 4 && table.id !== 3) {
                          tableStats.semifinal += 1
                        }
                      } else {
                        participant.stats.standard.for += _.toInteger(match.score)
                      }
                    } else {
                      if (match.score === 'V') {
                        participant.stats.standard.against += maxScore
                        participant.stats.standard.loses += 1
                      } else if (_.first(match.score) === 'V') {
                        participant.stats.standard.against += _.toInteger(match.score.slice(1))
                        participant.stats.standard.loses += 1
                      } else {
                        participant.stats.standard.against += _.toInteger(match.score)
                      }
                    }
                  })
                })
              })
            }
          })

          console.log(`b${participant.last_name}_${tableStats.wins}`)
          participant.stats.eliminationWins = tableStats.wins
          participant.tournamentScore += tableStats.wins * (tournamentBracketWinValue / 2)
        }
      })

      participant.tournamentScore += participant.poolScore
      const tournamentRatio = participant.tournamentScore / (100 + bracketPoints)

      participant.tournamentPoints = Math.trunc(tournamentRatio * tournamentValue)

      if (_.isUndefined(lowestTournamentPoints) || (lowestTournamentPoints > participant.tournamentPoints)) {
        lowestTournamentPoints = participant.tournamentPoints
      }

      return participant
    })

    const featWinners = calculateFeats(calculatedRawParticipants)
    setAwardedFeats(featWinners)

    _.each(featWinners, (feat) => {
      _.each(feat.winners, (winner) => {
        _.find(calculatedRawParticipants, { id: winner.id }).feats.push(feat.title)
      })
    })

    const calculatedParticipants = calculatedRawParticipants.map(participant => {
      participant.tournamentPointsAdjusted = (10 - lowestTournamentPoints) + participant.tournamentPoints

      return participant
    })

    const sortedParticipants = calculatedParticipants.sort((a, b) => {
      if (a.place === b.place) {
        if (a.stats.standard.wins === b.stats.standard.wins) {
          return a.tournamentPoints > b.tournamentPoints ? -1 : 1
        } else {
          return a.stats.standard.wins > b.stats.standard.wins ? -1 : 1
        }
      } else {
        return a.place > b.place ? 1 : -1
      }
    }).map((participant, index) => {
      return Object.assign({}, participant, { place: index + 1 })
    })

    const newTournament = JSON.parse(JSON.stringify(props.tournament))
    newTournament.participants = sortedParticipants
    const promotedTournament = promoteParticipants(newTournament)

    setTournament(promotedTournament)
    setSorted(promotedTournament.participants)
    setIsLoaded(true)
    // eslint-disable-next-line
  }, [props.tournament])

  const handleExportHtml = () => {
    const basePage = document.implementation.createHTMLDocument()
    basePage.head.innerHTML = document.head.innerHTML
    basePage.body.innerHTML = document.getElementById('final').outerHTML

    const pageHTML = basePage.documentElement.outerHTML
    const file = new window.Blob([pageHTML], { type: 'text/html' })

    const element = document.createElement('a')
    element.href = URL.createObjectURL(file)
    element.download = `${tournament.title}_export.html`
    document.body.appendChild(element) // Required for this to work in FireFox
    element.click()
  }

  const prepareData = () => {
    const dataToExport = {
      tournament: {},
      participants: tournament.participants
    }

    dataToExport.tournament = _.pick(tournament, ['uuid', 'title', 'web_site', 'weapon', 'catgory', 'gender', 'level', 'location', 'organizer', 'date', 'checkin', 'scratch', 'schedule', 'rating'])
    dataToExport.tournament.rounds = tournament.schedule.map((item, index) => {
      if (['pools', 'table', 'bestof'].includes(item)) {
        const config = buildConfig(tournament, item, index)

        const round = item === 'bestof' ? 'table' : item
        const result = tournament[`${round}_${index}`]

        return {
          type: item,
          config,
          result
        }
      }
      return { type: item }
    })

    return dataToExport
  }

  const handleExportData = () => {
    const dataToExport = prepareData()

    const file = new window.Blob([JSON.stringify(dataToExport)], { type: 'application/json' })

    const element = document.createElement('a')
    element.href = URL.createObjectURL(file)
    element.download = `${tournament.title}_export.json`
    document.body.appendChild(element) // Required for this to work in FireFox
    element.click()
  }

  const handleUploadData = async () => {
    const dataToExport = prepareData()

    await window.fetch('https://kdta9hzh3m.execute-api.us-east-1.amazonaws.com/personal/api/tournament/upload', {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(dataToExport)
    })

    notification.success({
      message: 'Tournament Data Uploaded Successfully',
      description: 'The tournament data has been uploaded an is currently being processed.'
    })
  }

  const filterColumnsList = (value) => {
    setIsResultColumnSelectorVisible(!isResultColumnSelectorVisible)
  }

  const selectResultField = (e, value) => {
    const newFilters = JSON.parse(JSON.stringify(resultColumns))

    setResultColumns(newFilters.map(f => {
      if (value === f.dataIndex) {
        f.display = e.target.checked
      }

      return f
    }))
  }

  const handleDisplayGraphs = () => {
    setIsResultGraphVisible(!isResultGraphVisible)
  }

  const handleDisplayFeats = () => {
    setIsResultFeatsVisible(!isResultFeatsVisible)
  }

  const handleHonorList = () => {
    setIsResultHonorVisible(!isResultHonorVisible)
  }

  return (
    <div>
      <div className='tournament-menu tournament-final'>
        <Row gutter={[8]}>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handleDisplayGraphs}>
              <PieChartOutlined />
              Graph
            </Button>
          </Col>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handleDisplayFeats}>
              <PieChartOutlined />
              Feats
            </Button>
          </Col>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handleHonorList}>
              <PieChartOutlined />
              Raffle Ticket List
            </Button>
          </Col>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handlePrintFinal}>
              <PrinterOutlined />
              Print
            </Button>
          </Col>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handleExportHtml}>
              <Html5Outlined />
              Export to HTML
            </Button>
          </Col>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handleExportData}>
              <ExportOutlined />
              Export Data
            </Button>
          </Col>
          <Col flex='none'>
            <Button block shape='round' className='menu-btn' onClick={handleUploadData}>
              <CloudUploadOutlined />
              Upload Data
            </Button>
          </Col>
          <Col flex='auto' style={{ textAlign: 'right', paddingTop: '.2em' }}>
            <Dropdown
              overlay={<Menu
                items={resultColumns.map(c => Object.assign({ label: <Checkbox checked={c.display} onChange={(e) => selectResultField(e, c.dataIndex)}>{c.title}</Checkbox>, key: c.dataIndex }))}
                       />}
              onVisibleChange={filterColumnsList}
              visible={isResultColumnSelectorVisible}
              trigger={['click']}
            >
              <a href onClick={(e) => e.preventDefault()}>
                <Space>
                  Show/Hide columns
                  <DownOutlined />
                </Space>
              </a>
            </Dropdown>
          </Col>
        </Row>
      </div>
      <div style={{ marginTop: '1em', textAlign: 'center' }}><h2>Tournament Rating: {tournament.rating}</h2></div>
      <Table dataSource={sorted} columns={resultColumns.filter(c => c.display)} key='uuid' pagination={false} scroll={dynamicSizing} size='small' />
      {isResultGraphVisible
        ? (
          <GraphFinal tournament={tournament} participants={sorted} />
          )
        : ''}
      <div style={{ display: 'none' }}>
        {isLoaded
          ? (
            <PrintFinal ref={printFinalRef} tournament={tournament} participants={sorted} />
            )
          : ''}
      </div>
      {isResultFeatsVisible
        ? (
          <Feats awards={awardedFeats} />
          )
        : ''}
      {isResultHonorVisible
        ? (
          <RaffleListDisplay participants={sorted} />
          )
        : ''}
    </div>
  )
}

export default TournamentFinal
